import { Plus } from 'lucide-react';

import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { WEEKDAYS } from '../constants';
import { coversDay, fromISO, monthCells, todayISO } from '../utils/dates';
import { EventChip } from './EventChip';
import type { CalendarEventItem } from '../types/calendar';

/** How many entries a day cell shows before it starts counting the rest. */
const CELL_LIMIT = 3;

export interface MonthGridProps {
  /** Any day inside the month being drawn. */
  cursor: string;
  events: CalendarEventItem[];
  onSelectEvent: (item: CalendarEventItem) => void;
  /** Start a new entry on that day. Absent on a read-only calendar. */
  onAddOn?: (iso: string) => void;
  /** Open one day on its own — where the cell has run out of room. */
  onOpenDay: (iso: string) => void;
}

/**
 * The month, as a wall calendar.
 *
 * Multi-day entries repeat in every day they cover rather than only the day
 * they open on: a reader looking at the 22nd of a vacation needs to see that
 * the institute is shut, not an empty square.
 */
export function MonthGrid({
  cursor,
  events,
  onSelectEvent,
  onAddOn,
  onOpenDay,
}: MonthGridProps) {
  const anchor = fromISO(cursor);
  const cells = monthCells(anchor.getFullYear(), anchor.getMonth());
  const month = cursor.slice(0, 7);
  const today = todayISO();

  return (
    // The seven columns cannot compress below a legible width, so on a phone
    // the grid scrolls inside the panel instead of the page scrolling sideways.
    <div className="overflow-x-auto">
      <div className="min-w-[46rem]">
        <div className="grid grid-cols-7 rounded-t-2xl border border-line bg-surface-2">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="border-l border-line px-2 py-2.5 text-center text-[11px] font-black uppercase tracking-wider text-ink-3 first:border-l-0"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 rounded-b-2xl border-x border-b border-line">
          {cells.map((iso) => {
            const isOutside = !iso.startsWith(month);
            const isToday = iso === today;
            const dayEvents = events.filter((item) => coversDay(item, iso));
            const hidden = dayEvents.length - CELL_LIMIT;

            return (
              <div
                key={iso}
                className={cn(
                  'group relative flex min-h-[7.5rem] flex-col gap-1 border-l border-t border-line p-1.5 transition-colors [&:nth-child(7n+1)]:border-l-0',
                  isOutside ? 'bg-surface-2' : 'bg-surface hover:bg-surface-2',
                )}
              >
                <div className="flex items-center justify-between gap-1 px-0.5">
                  <span
                    className={cn(
                      'grid h-6 min-w-[1.5rem] place-items-center rounded-full px-1 text-[12px] font-black tabular-nums',
                      isToday && 'bg-accent text-white',
                      !isToday && isOutside && 'text-ink-3',
                      !isToday && !isOutside && 'text-ink',
                    )}
                  >
                    {Number(iso.slice(8, 10))}
                  </span>

                  {onAddOn && (
                    <button
                      type="button"
                      aria-label={`Add an entry on ${formatDate(iso)}`}
                      onClick={() => onAddOn(iso)}
                      className="grid h-5 w-5 cursor-pointer place-items-center rounded-md text-ink-3 opacity-0 transition-opacity hover:bg-accent-soft hover:text-accent focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <Plus className="h-3.5 w-3.5" aria-hidden />
                    </button>
                  )}
                </div>

                {dayEvents.slice(0, CELL_LIMIT).map((item) => (
                  <EventChip key={item.uuid} item={item} onSelect={onSelectEvent} />
                ))}

                {hidden > 0 && (
                  <button
                    type="button"
                    onClick={() => onOpenDay(iso)}
                    className="cursor-pointer rounded-md px-2 py-0.5 text-left text-[10.5px] font-bold text-accent hover:bg-accent-soft"
                  >
                    +{hidden} more
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
